import type { ClientListFacets, ClientListItem, ClientMergePreview } from "@closer/shared";
import { Button, Checkbox, Input, Modal, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * MÜKERRER KAYIT BİRLEŞTİRME ONAYI (CLOS-171 `[118]`, ADR-0030).
 *
 * ### ⚠ HANGİSİNİN KAPANACAĞI cümleyle yazıyor
 *
 * Kart: *"kullanıcı survivor ve kapanacak kaynak kaydı AÇIKÇA seçer; geri alınamaz etkiler
 * işlem öncesi gösterilir."* İki adı yan yana koyup okla göstermek yetmez — hangisinin
 * kaybolacağı tam cümleyle söyleniyor. Yanlış yönde yapılmış bir birleştirme geri alınamaz.
 *
 * ### ⚠ Eşleşme SKORU yok
 *
 * ADR-0030: yüzde, olmayan bir kesinlik iddia eder ve kullanıcı düşünmeyi bırakır. Onun
 * yerine hangi SİNYALİN eşleştiği yazıyor ("aynı kimlik numarası") — kullanıcı neye
 * baktığını biliyor, paylaşılan bir ofis telefonunu da fark edebiliyor.
 *
 * ### ⚠ Onay KUTUCUKLA
 *
 * Geri alınamaz bir işlem için tek tık yetmiyor; kullanıcı etkiyi okuduğunu işaretliyor.
 */

const SINYAL_ADLARI: Record<ClientMergePreview["matchedOn"][number], string> = {
  identity: "aynı kimlik/vergi numarası",
  email: "aynı e-posta",
  phone: "aynı telefon",
};

export function ClientMergeDialog({
  targetId,
  targetName,
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  targetId: string;
  targetName: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (girdi: { sourceId: string; sourceVersion: number; targetVersion: number }) => void;
  isPending: boolean;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const [arama, setArama] = useState("");
  const [secilen, setSecilen] = useState<ClientListItem | null>(null);
  const [onaylandi, setOnaylandi] = useState(false);

  /**
   * Aday arama LİSTE ucundan: ayrı bir "mükerrer bul" ucu yazmak, aynı erişim kurallarının
   * ikinci bir kopyasını üretirdi (`[116]` zaten süzüyor ve mezar taşlarını dışarıda
   * tutuyor).
   */
  const adaylar = useQuery({
    enabled: open && arama.trim().length > 0,
    queryKey: tenantKey(tenantId, "client-merge-adaylar", arama),
    queryFn: async () =>
      (
        await api<{ items: ClientListItem[]; facets: ClientListFacets }>(
          `/api/clients?limit=10&q=${encodeURIComponent(arama.trim())}`,
          { tenantId },
        )
      ).data.items.filter((m) => m.id !== targetId),
  });

  const onizleme = useQuery({
    enabled: open && secilen !== null,
    queryKey: tenantKey(tenantId, "client-merge-onizleme", targetId, secilen?.id ?? ""),
    queryFn: async () =>
      (
        await api<ClientMergePreview>(`/api/clients/${targetId}/merge/preview`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({ sourceId: secilen!.id }),
        })
      ).data,
  });

  function kapat() {
    setSecilen(null);
    setOnaylandi(false);
    setArama("");
    onClose();
  }

  const veri = onizleme.data;

  return (
    <Modal
      open={open}
      onClose={kapat}
      title="Mükerrer kaydı birleştir"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={kapat}>
            Vazgeç
          </Button>
          {/* Önizleme gelmeden düğme YOK: etkiyi görmeden onaylanabilen bir işlem olmamalı. */}
          {veri && (
            <Button
              type="button"
              loading={isPending}
              disabled={!onaylandi}
              onClick={() =>
                onConfirm({
                  sourceId: veri.source.id,
                  sourceVersion: veri.source.version,
                  targetVersion: veri.target.version,
                })
              }
            >
              Birleştir
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          aria-label="Kapanacak kaydı ara"
          placeholder="Kapanacak kaydın adı veya e-postası"
          value={arama}
          onChange={(e) => {
            setArama(e.target.value);
            setSecilen(null);
            setOnaylandi(false);
          }}
        />

        {adaylar.isFetching && (
          <div role="status" aria-label="Adaylar aranıyor">
            <Skeleton lines={2} />
          </div>
        )}

        {!secilen && (adaylar.data ?? []).length > 0 && (
          <ul className="flex flex-col gap-1">
            {(adaylar.data ?? []).map((aday) => (
              <li key={aday.id}>
                <Button
                  variant="secondary"
                  onClick={() => setSecilen(aday)}
                  className="w-full !justify-start !text-left"
                >
                  {aday.displayName}
                  {aday.email ? ` · ${aday.email}` : ""}
                </Button>
              </li>
            ))}
          </ul>
        )}

        {secilen && onizleme.isPending && (
          <div role="status" aria-label="Birleştirme etkileri yükleniyor">
            <Skeleton lines={3} />
          </div>
        )}

        {secilen && onizleme.error && (
          <StateView
            state={resolveErrorState(onizleme.error, { idempotent: true })}
            onRetry={() => void onizleme.refetch()}
          />
        )}

        {veri && (
          <div className="flex flex-col gap-3 text-sm">
            {/*
              ⚠ Yön TAM CÜMLEYLE. Ok işareti ("A → B") hızlı okunduğunda ters
              anlaşılabilir ve yanlış yönde yapılmış bir birleştirme geri alınamaz.
            */}
            <p className="text-fg">
              <strong>{veri.source.displayName}</strong> kaydı kapanacak ve{" "}
              <strong>{targetName}</strong> kaydına birleşecek. Bu işlem geri alınamaz.
            </p>

            {veri.matchedOn.length > 0 && (
              <p className="text-fg-soft">
                Eşleşen bilgiler: {veri.matchedOn.map((s) => SINYAL_ADLARI[s]).join(", ")}.
              </p>
            )}

            {/*
              ⚠ SIFIR taşınacak sınıf yazılmıyor. "Portföydeki sözleşme: 0 kayıt taşınacak"
              satırı kullanıcıya hiçbir şey söylemiyor ama geri alınamaz bir onay ekranını
              gürültüyle dolduruyor — okunması gereken cümle (hangi kayıt kapanacak) o
              gürültünün içinde kayboluyor. Arşiv diyaloğunda (`[119]`) baştan böyleydi;
              buradaki eksiği ekranı elle gezerken gördüm.
            */}
            {veri.references.some((r) => r.count > 0) && (
              <ul className="flex flex-col gap-1 text-fg-soft">
                {veri.references
                  .filter((r) => r.count > 0)
                  .map((r) => (
                    <li key={r.key}>
                      {r.label}: {r.count} kayıt taşınacak
                    </li>
                  ))}
              </ul>
            )}

            {veri.warnings.map((uyari) => (
              <p key={uyari} className="text-fg-soft">
                {uyari}
              </p>
            ))}

            <Checkbox
              label="Kapanacak kaydı ve geri alınamaz etkileri okudum"
              checked={onaylandi}
              onChange={(e) => setOnaylandi(e.target.checked)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
